import { requestAPI, updateAPI } from '../../../../utils/request';
import {
  privacyPoliciesRequest,
  privacyPoliciesSuccess,
  privacyPoliciesFailure,
  setDisplayPrivacyPolicies,
  privacyPolicyUpdateRequest,
  privacyPolicyUpdateSuccess,
  privacyPolicyUpdateFailure,
  privacyPolicyRequest,
  privacyPolicySuccess,
  privacyPolicyFailure,
  purposeSuccess,
  purposeUpdateRequest,
  purposeUpdateSuccess,
  purposeUpdateFailure,
  copyPrivacyPolicySuccess,
  activationEventsRequest,
  activationEventsSuccess,
  activationEventsFailure,
  groupsRequest,
  groupsSuccess,
  groupsFailure,
  createPurposeSuccess,
  createPurposesBatchSuccess,
  archivePurposeSuccess,
  collectionNamesFailure,
  collectionNamesRequest,
  collectionNamesSuccess,
} from './actions';
import message from '../../../../utils/message';
import { Dispatch, GetState } from '../../../../@types';
import { PricingState } from '../../../../@types/enums';

const fetchPrivacyPolicies = () => (dispatch: Dispatch) => {
  dispatch(privacyPoliciesRequest());
  requestAPI('/fleets/privacy/policies')
    .then((privacyPolicies) => {
      dispatch(privacyPoliciesSuccess(privacyPolicies));
      dispatch(setDisplayPrivacyPolicies(privacyPolicies));
    })
    .catch((err) => {
      dispatch(privacyPoliciesFailure(err));
      message.error(err.message);
    });
};

const shouldFetchPrivacyPolicies = (state: PricingState) => !state.loading;

export const fetchPrivacyPoliciesIfNeeded = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    if (shouldFetchPrivacyPolicies(getState().appData.privacyPolicies)) {
      return dispatch(fetchPrivacyPolicies());
    } else {
      return Promise.resolve();
    }
  };
};

const fetchCollectionNames = () => (dispatch: Dispatch) => {
  dispatch(collectionNamesRequest());
  requestAPI('/fleets/privacy/collectionNames')
    .then((collectionNames) => {
      dispatch(collectionNamesSuccess(collectionNames));
    })
    .catch((err) => {
      dispatch(collectionNamesFailure(err));
      message.error(err.message);
    });
};

const shouldFetchCollectionNames = (state: PricingState) =>
  !state?.collectionNames;

export const fetchCollectionNamesIfNeeded = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    if (shouldFetchCollectionNames(getState().appData.privacyPolicies)) {
      return dispatch(fetchCollectionNames());
    } else {
      return Promise.resolve();
    }
  };
};

const fetchActivationEvents = () => (dispatch: Dispatch) => {
  dispatch(activationEventsRequest());
  requestAPI('/fleets/privacy/activationEvents')
    .then((activationEvents) => {
      dispatch(activationEventsSuccess(activationEvents));
    })
    .catch((err) => {
      dispatch(activationEventsFailure(err));
      message.error(err.message);
    });
};

const shouldFetchActivationEvents = (state: PricingState) =>
  !state?.activationEvents;

export const fetchActivationEventsIfNeeded = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    if (shouldFetchActivationEvents(getState().appData.privacyPolicies)) {
      return dispatch(fetchActivationEvents());
    } else {
      return Promise.resolve();
    }
  };
};

const fetchGroups = () => (dispatch: Dispatch) => {
  dispatch(groupsRequest());
  requestAPI('/fleets/privacy/fieldGroups')
    .then((activationEvents) => {
      dispatch(groupsSuccess(activationEvents));
    })
    .catch((err) => {
      dispatch(groupsFailure(err));
      message.error(err.message);
    });
};

const shouldFetchGroups = (state: PricingState) => !state?.groups;

export const fetchGroupsIfNeeded = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    if (shouldFetchGroups(getState().appData.privacyPolicies)) {
      return dispatch(fetchGroups());
    } else {
      return Promise.resolve();
    }
  };
};

export const fetchPrivacyPolicyById = (id: string) => (dispatch: Dispatch) => {
  dispatch(privacyPolicyRequest());

  requestAPI(`/fleets/privacy/policies/${id}`)
    .then((res) => {
      dispatch(privacyPolicySuccess(res));
    })
    .catch((err) => {
      dispatch(privacyPolicyFailure(err));
      message.error(err.message);
    });
};

export const fetchPurposeById = (id: string) => (dispatch: Dispatch) => {
  requestAPI(`/fleets/privacy/processingPurposes/${id}`)
    .then((res) => {
      dispatch(purposeSuccess(res));
    })
    .catch((err) => {
      message.error(err.message);
    });
};

export const publishPrivacyPolicy = (id: string) => (dispatch: Dispatch) => {
  dispatch(privacyPolicyRequest());

  requestAPI(`/fleets/privacy/policies/${id}/publish`, {}, { method: 'PUT' })
    .then((res) => {
      if (res?.ok === undefined) {
        dispatch(privacyPolicySuccess(res));
        message.success('Published!');
      }
    })
    .catch((err) => {
      dispatch(privacyPolicyFailure(err));
      message.error(err.message);
    });
};

export const archivePrivacyPolicy = (id: string) => (dispatch: Dispatch) => {
  dispatch(privacyPolicyUpdateRequest());

  requestAPI(`/fleets/privacy/policies/${id}/archive`, {}, { method: 'PUT' })
    .then((res) => {
      if (res?.ok === undefined) {
        dispatch(privacyPolicyUpdateSuccess(res));
        message.success('Archived!');
      } else {
        message.error('Error');
      }
    })
    .catch((err) => {
      dispatch(privacyPolicyUpdateFailure(err));
      message.error(err.message);
    });
};

export const copyPrivacyPolicy = (id: string) => (dispatch: Dispatch) => {
  dispatch(privacyPolicyRequest());

  requestAPI(`/fleets/privacy/policies/${id}/copy`, {}, { method: 'POST' })
    .then((res) => {
      if (res?.ok === undefined) {
        dispatch(copyPrivacyPolicySuccess(res));
        message.success('Copied!');
      }
    })
    .catch((err) => {
      dispatch(privacyPolicyFailure(err));
      message.error(err.message);
    });
};

export const archivePurpose = (purposeId: string) => (dispatch: Dispatch) => {
  requestAPI(
    `/fleets/privacy/processingPurposes/${purposeId}/archive`,
    {},
    { method: 'PUT' }
  )
    .then((res) => {
      if (res?.ok === undefined) {
        dispatch(archivePurposeSuccess({ purposeId }));
        message.success('Archived!');
      }
    })
    .catch((err) => {
      message.error(err.message);
    });
};

// Archive multiple purposes with a single notification
export const archivePurposes =
  (purposeIds: string[]) => async (dispatch: Dispatch) => {
    const results = await Promise.allSettled(
      purposeIds.map((purposeId) =>
        requestAPI(
          `/fleets/privacy/processingPurposes/${purposeId}/archive`,
          {},
          { method: 'PUT' }
        ).then((res) => ({ res, purposeId }))
      )
    );

    const successes = results.filter(
      (r) => r.status === 'fulfilled' && r.value?.res?.ok === undefined
    );
    const failures = results.filter(
      (r) => r.status === 'rejected' || r.value?.res?.ok !== undefined
    );

    // Dispatch success for each archived purpose
    successes.forEach((r) => {
      if (r.status === 'fulfilled') {
        dispatch(archivePurposeSuccess({ purposeId: r.value.purposeId }));
      }
    });

    // Show single notification
    if (failures.length === 0 && successes.length > 0) {
      message.success('Archived!');
    } else if (failures.length > 0 && successes.length === 0) {
      message.error('Failed to archive purpose');
    } else if (failures.length > 0 && successes.length > 0) {
      message.success('Archived!');
    }

    return { successes: successes.length, failures: failures.length };
  };

export const updatePurpose = updateAPI(
  purposeUpdateRequest,
  purposeUpdateSuccess,
  purposeUpdateFailure,
  '/fleets/privacy/processingPurposes/'
);

export const createPurpose = (purposePayload: any) => (dispatch: Dispatch) => {
  requestAPI(
    `/fleets/privacy/processingPurposes`,
    {},
    { method: 'POST', body: JSON.stringify(purposePayload) }
  )
    .then((res) => {
      if (res?.ok === undefined) {
        dispatch(createPurposeSuccess(res));
        message.success('Created!');
      }
    })
    .catch((err) => {
      message.error(err.message);
    });
};

// Create multiple purposes with a single notification
export const createPurposes =
  (purposePayloads: any[]) => async (dispatch: Dispatch) => {
    if (purposePayloads.length === 0) {
      return { successes: 0, failures: 0 };
    }

    const policyId = purposePayloads[0].policyId;

    const results = await Promise.allSettled(
      purposePayloads.map((payload) =>
        requestAPI(
          `/fleets/privacy/processingPurposes`,
          {},
          { method: 'POST', body: JSON.stringify(payload) }
        )
      )
    );

    const successes = results.filter(
      (r) => r.status === 'fulfilled' && r.value?.ok === undefined
    );
    const failures = results.filter(
      (r) => r.status === 'rejected' || r.value?.ok !== undefined
    );

    // Dispatch batch success with all created purposes
    if (successes.length > 0) {
      const createdPurposes = successes
        .filter((r) => r.status === 'fulfilled')
        .map((r) => (r as PromiseFulfilledResult<any>).value);

      dispatch(
        createPurposesBatchSuccess({
          purposes: createdPurposes,
          policyId,
        })
      );
    }

    // Show single notification (admin sees it as one purpose)
    if (failures.length === 0 && successes.length > 0) {
      message.success('Created!');
    } else if (failures.length > 0 && successes.length === 0) {
      message.error('Failed to create purpose');
    } else if (failures.length > 0 && successes.length > 0) {
      // Partial failure - still show success since some were created
      message.success('Created!');
    }

    return { successes: successes.length, failures: failures.length };
  };

// Update multiple purposes with a single notification
export const updatePurposes =
  (updates: { id: string; payload: any }[]) => async (dispatch: Dispatch) => {
    const results = await Promise.allSettled(
      updates.map(({ id, payload }) =>
        requestAPI(
          `/fleets/privacy/processingPurposes/${id}`,
          {},
          { method: 'PUT', body: JSON.stringify(payload) }
        ).then((res) => ({ res, id }))
      )
    );

    const successes = results.filter(
      (r) => r.status === 'fulfilled' && r.value?.res?.ok === undefined
    );
    const failures = results.filter(
      (r) => r.status === 'rejected' || r.value?.res?.ok !== undefined
    );

    // Dispatch success for each updated purpose
    successes.forEach((r) => {
      if (r.status === 'fulfilled') {
        dispatch(purposeUpdateSuccess(r.value.res));
      }
    });

    // Show single notification
    if (failures.length === 0 && successes.length > 0) {
      message.success('Updated!');
    } else if (failures.length > 0 && successes.length === 0) {
      message.error('Failed to update purpose');
    } else if (failures.length > 0 && successes.length > 0) {
      message.success('Updated!');
    }

    return { successes: successes.length, failures: failures.length };
  };
